import { trigger, transition, style, animate, state } from '@angular/animations';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { SharedService } from '@shared/services/shared.service';
import { IComplaint } from 'app/models/compliance/complaint';
import { IIHCAgency } from 'app/models/compliance/ihc-agency';
import { IAffectedPerson } from 'app/models/compliance/affected-person';
import { ComplaintService } from 'app/services/compliance/complaint.service';
import { AffectedPersonDialogComponent } from '../affected-person/dialog-affected-person.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize, map, switchMap } from 'rxjs';

@Component({
  selector: 'app-complaint',
  templateUrl: './complaint.component.html',
  styleUrl: './complaint.component.scss',
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ]),
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})

export class ComplaintComponent implements OnInit {
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() licenseId: string = '';

  public editing: boolean = false;
  public adding: boolean = false;
  public creating: boolean = false;
  public saving: boolean = false;
  public showClosedComplaints: boolean = false;
  public complaints: IComplaint[] = [];
  public ihcAgencies: IIHCAgency[] = [];
  public complaint: IComplaint = {
    id: 0,
    complaintId: '',
    entityId: 0,
    entity: '',
    otherEntity: '',
    entityGuid: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    complainantAddress: '',
    complainantIsClient: null,
    employeeStatus: '',
    neverAnEmployee: null,
    workForAPS: null,
    apsAgency: '',
    incidentDates: '',
    description: '',
    summary: '',
    summaryOther: '',
    othersWithKnowledge: '',
    complaintSubmittedAgency: '',
    complaintSubmittedOther: '',
    complaintSubmittedPolice: '',
    priority: '',
    closed: false,
    unlicensedAgency: false,
    enteredByStaff: null,
    requiresInvestigation: false,
    reasonForInvestigation: '',
    investigationNarrative: '',
    investigationDueDate: null,
    triageDate: null,
    triageDecision: '',
    investigationEntranceDate: null,
    investigationExitDate: null,
    investigationEndDate: null,
    dateReportSent: null,
    planOfCorrectionDue: null,
    affectedPeople: []
  }

  public complaintForm = new FormGroup({
    entityId: new FormControl(0),
    entity: new FormControl(''),
    otherEntity: new FormControl(''),
    entityGuid: new FormControl(''),
    firstName: new FormControl(''),
    middleName: new FormControl(''),
    lastName: new FormControl(''),
    phone: new FormControl(''),
    email: new FormControl(''),
    complainantAddress: new FormControl(''),
    complainantIsClient: new FormControl(false),
    employeeStatus: new FormControl(''),
    neverAnEmployee: new FormControl(false),
    workForAPS: new FormControl(false),
    apsAgency: new FormControl(''),
    incidentDates: new FormControl(''),
    description: new FormControl(''),
    summary: new FormControl<string[]>([]),
    summaryOther: new FormControl(''),
    othersWithKnowledge: new FormControl(''),
    complaintSubmittedAgency: new FormControl(''),
    complaintSubmittedOther: new FormControl(''),
    complaintSubmittedPolice: new FormControl(''),
    priority: new FormControl(''),
    closed: new FormControl(false),
    unlicensedAgency: new FormControl(false),
    enteredByStaff: new FormControl<boolean | null>(null),
    requiresInvestigation: new FormControl(false),
    reasonForInvestigation: new FormControl(''),
    investigationNarrative: new FormControl(''),
    investigationDueDate: new FormControl<Date | null>(null),
    investigationDueInDays: new FormControl<number | null>(null),
    triageDate: new FormControl<Date | null>(null),
    triageDecision: new FormControl(''),
    investigationEntranceDate: new FormControl<Date | null>(null),
    investigationExitDate: new FormControl<Date | null>(null),
    investigationEndDate: new FormControl<Date | null>(null),
    dateReportSent: new FormControl<Date | null>(null),
    planOfCorrectionDue: new FormControl<Date | null>(null),
  })

  public personDialogRef!: MatDialogRef<AffectedPersonDialogComponent>;
  public dialog = inject(MatDialog);
  public sharedService = inject(SharedService);
  public complaintService = inject(ComplaintService);
  public route = inject(ActivatedRoute);

  public complaintColumns: string [] = ['complaintId', 'entityGuid', 'entity', 'unlicensedAgency', 'summary', 'closed'];
  public complaintSource = new MatTableDataSource<IComplaint>(this.complaints);
  @ViewChild('complaintPaginator', {static: false}) complaintPaginator!: MatPaginator;
  @ViewChild('complaintSort', {static: false}) complaintSort!: MatSort;

  public affectedPeopleSource = new MatTableDataSource<IAffectedPerson>(this.complaint.affectedPeople);
  public affectedPeopleColumns: string[] = ['name', 'phone', 'email', 'relationship', 'canContact'];
  public affectedPeopleEditColumns: string[] = ['name', 'phone', 'email', 'relationship', 'actions'];
  public expandedPerson: IAffectedPerson | null = null;

  constructor() {
    this.entityType = this.route.snapshot.paramMap.get('entity') || '';
  }

  ngOnInit(): void {
    this.loadComplaints();
    this.loadIHCAgencies();
  }

  public loadComplaints(): void {
    this.complaintService.getComplaints(this.entityType, this.entityId).subscribe({
      next: (response: IComplaint[]) => this.complaints = response,
      error: (error) => {
        console.log("Error loading complaints", error);
      },
      complete: () => this.updateComplaintTable()
    });
  }

  public loadIHCAgencies(): void {
    this.complaintService.getIHCAgencies().subscribe({
      next: (response: IIHCAgency[]) => this.ihcAgencies = response,
      error: (error) => {
        console.log("Error loading IHC Agencies", error);
      }
    });
  }

  private updateComplaintTable(): void {
    setTimeout(() => {
      this.complaintSource.data = this.complaints.filter(complaint => this.showClosedComplaints || !complaint.closed);
      this.complaintSource.paginator = this.complaintPaginator;
      this.complaintSource.sort = this.complaintSort;
    }, 500);
  }

  private updateAffectedPeopleTable(): void {
    this.affectedPeopleSource.data = this.complaint.affectedPeople;
  }

  public toggleClosedComplaints(): void {
    this.showClosedComplaints = !this.showClosedComplaints;
    this.complaintPaginator?.firstPage();
    this.updateComplaintTable();
  }

  public addComplaint(): void {
    if (this.creating) {
      return;
    }
    this.creating = true;
    this.complaintService.createComplaint().pipe(
      finalize(() => this.creating = false)
    ).subscribe({
      next: response => {
        this.complaint = {
          ...this.getEmptyComplaint(),
          id: response.id,
          complaintId: response.complaintId,
          entityId: this.entityId,
          entityGuid: this.licenseId,
          enteredByStaff: true
        };
        const agency = this.ihcAgencies.find(item => item.agencyId === this.entityId);
        if (agency) {
          this.complaint.entity = agency.agencyName;
        }
        this.adding = true;
        this.editing = true;
        this.updateComplaintForm(this.complaint);
        this.updateAffectedPeopleTable();
      },
      error: () => this.sharedService.toastr.error('Could not create complaint.')
    });
  }

  public setComplaintEntityId(): void {
    if (!this.adding || this.entityId > 0) {
      return;
    }
    const entityGuid = this.complaintForm.controls.entityGuid.value?.trim() ?? '';
    const agency = this.ihcAgencies.find(item => item.agencyGuid === entityGuid);
    this.complaintForm.patchValue({
      entityGuid,
      entityId: agency?.agencyId ?? 0,
      ...(agency ? { entity: agency.agencyName, unlicensedAgency: false } : {})
    });
  }

  public selectComplaint(complaint: IComplaint): void {
    this.complaint = complaint;
    this.updateComplaintForm(complaint);
    this.updateAffectedPeopleTable();
  }

  public toggleEdit(): void {
    this.editing = !this.editing;
  }

  public updateInvestigationDueDate(): void {
    const days = this.complaintForm.controls.investigationDueInDays.value;
    if (days === null || !Number.isSafeInteger(days) || days < 0) {
      return;
    }

    const dueDate = new Date();
    dueDate.setHours(0, 0, 0, 0);
    dueDate.setDate(dueDate.getDate() + days);
    if (!Number.isNaN(dueDate.getTime())) {
      this.complaintForm.controls.investigationDueDate.setValue(dueDate);
      this.complaintForm.controls.investigationDueDate.markAsDirty();
    }
  }

  public closeComplaint(): void {
    this.complaint = this.getEmptyComplaint();
    this.updateComplaintForm(this.complaint);
    this.editing = false;
    this.adding = false;
    this.updateComplaintTable();
  }

  public selectAgency(entity: string): void {
    if (this.adding) {
      // New complaints use the license context or the manually entered Entity ID.
      this.complaintForm.patchValue({ unlicensedAgency: entity === 'Other' });
      return;
    }
    if(entity === 'Other'){
      this.complaintForm.patchValue({ entityGuid: '', entityId: 0, unlicensedAgency: true });
      return;
    }
    let agency: IIHCAgency = this.ihcAgencies.find(a => a.agencyName === entity)!;
    if (agency) {
      this.complaintForm.patchValue({ entityGuid: agency.agencyGuid, entityId: agency.agencyId, unlicensedAgency: false });
    } else {
      this.complaintForm.patchValue({ entityGuid: '', entityId: 0, unlicensedAgency: true });
    }
  }

  private getEmptyComplaint(): IComplaint {
    let emptyComplaint: IComplaint = {
      id: 0,
      complaintId: '',
      entityId: 0,
      entity: '',
      otherEntity: '',
      entityGuid: '',
      firstName: '',
      middleName: '',
      lastName: '',
      phone: '',
      email: '',
      complainantAddress: '',
      complainantIsClient: null,
      employeeStatus: '',
      neverAnEmployee: null,
      workForAPS: null,
      apsAgency: '',
      incidentDates: '',
      description: '',
      summary: '',
      summaryOther: '',
      othersWithKnowledge: '',
      complaintSubmittedAgency: '',
      complaintSubmittedOther: '',
      complaintSubmittedPolice: '',
      priority: '',
      closed: false,
      unlicensedAgency: false,
      enteredByStaff: null,
      requiresInvestigation: false,
      reasonForInvestigation: '',
      investigationNarrative: '',
      investigationDueDate: null,
      triageDate: null,
      triageDecision: '',
      investigationEntranceDate: null,
      investigationExitDate: null,
      investigationEndDate: null,
      dateReportSent: null,
      planOfCorrectionDue: null,
      affectedPeople: []
    }
    return emptyComplaint;
  }

  onSummaryChange(selectedValues: string[]): void {
    this.complaint.summary = JSON.stringify(selectedValues || []);
  }

  getSummaryArray(complaint: IComplaint): string[] {
    try {
      return complaint.summary ? JSON.parse(complaint.summary) : [];
    } catch {
      return [];
    }
  }

  isSummaryOther(): boolean {
    return this.complaintForm.get('summary')?.value?.includes('Other')!;
  }

  private updateComplaintForm(complaint: IComplaint): void {
    this.complaintForm.reset({
      entityId: complaint.entityId,
      entity: complaint.entity,
      otherEntity: complaint.otherEntity,
      entityGuid: complaint.entityGuid,
      firstName: complaint.firstName,
      middleName: complaint.middleName,
      lastName: complaint.lastName,
      phone: complaint.phone,
      email: complaint.email,
      complainantAddress: complaint.complainantAddress,
      complainantIsClient: complaint.complainantIsClient,
      employeeStatus: complaint.employeeStatus,
      neverAnEmployee: complaint.neverAnEmployee,
      workForAPS: complaint.workForAPS,
      apsAgency: complaint.apsAgency,
      incidentDates: complaint.incidentDates,
      description: complaint.description,
      summary: this.getSummaryArray(complaint),
      summaryOther: complaint.summaryOther,
      othersWithKnowledge: complaint.othersWithKnowledge,
      complaintSubmittedAgency: complaint.complaintSubmittedAgency,
      complaintSubmittedOther: complaint.complaintSubmittedOther,
      complaintSubmittedPolice: complaint.complaintSubmittedPolice,
      priority: complaint.priority,
      unlicensedAgency: complaint.unlicensedAgency,
      enteredByStaff: complaint.enteredByStaff,
      requiresInvestigation: complaint.requiresInvestigation,
      reasonForInvestigation: complaint.reasonForInvestigation,
      investigationNarrative: complaint.investigationNarrative,
      triageDecision: complaint.triageDecision,
    });

    const dateFields = ['investigationDueDate', 'triageDate', 'investigationEntranceDate', 'investigationExitDate', 'investigationEndDate', 'dateReportSent', 'planOfCorrectionDue'];
    dateFields.forEach(field => {
      if((complaint as any)[field]){
        this.complaintForm.get(field)?.setValue(new Date((complaint as any)[field]));
      }
    });
  }

  public updateComplaint(markClosed: boolean = false): void {
    if (this.saving) {
      return;
    }
    this.sharedService.openConfirm(markClosed
      ? 'Save changes and close this complaint?'
      : this.adding ? 'Save the new complaint?' : 'Update the complaint?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          const formValues = this.complaintForm.getRawValue();
          this.complaint.entityId = formValues.entityId ?? 0;
          this.complaint.entity = formValues.entity ?? '';
          this.complaint.otherEntity = formValues.otherEntity ?? '';
          this.complaint.entityGuid = formValues.entityGuid ?? '';
          this.complaint.firstName = formValues.firstName ?? '';
          this.complaint.middleName = formValues.middleName ?? '';
          this.complaint.lastName = formValues.lastName ?? '';
          this.complaint.phone = formValues.phone ?? '';
          this.complaint.email = formValues.email ?? '';
          this.complaint.complainantAddress = formValues.complainantAddress ?? '';
          this.complaint.complainantIsClient = formValues.complainantIsClient;
          this.complaint.employeeStatus = formValues.employeeStatus ?? '';
          this.complaint.neverAnEmployee = formValues.neverAnEmployee;
          this.complaint.workForAPS = formValues.workForAPS;
          this.complaint.apsAgency = formValues.apsAgency ?? '';
          this.complaint.incidentDates = formValues.incidentDates ?? '';
          this.complaint.description = formValues.description ?? '';
          this.complaint.summary = JSON.stringify(formValues.summary);
          this.complaint.summaryOther = formValues.summaryOther ?? '';
          this.complaint.othersWithKnowledge = formValues.othersWithKnowledge ?? '';
          this.complaint.complaintSubmittedAgency = formValues.complaintSubmittedAgency ?? '';
          this.complaint.complaintSubmittedOther = formValues.complaintSubmittedOther ?? '';
          this.complaint.complaintSubmittedPolice = formValues.complaintSubmittedPolice ?? '';
          this.complaint.priority = formValues.priority ?? '';
          this.complaint.unlicensedAgency = formValues.unlicensedAgency ?? false;
          this.complaint.enteredByStaff = formValues.enteredByStaff ?? null;
          this.complaint.requiresInvestigation = formValues.requiresInvestigation ?? false;
          this.complaint.reasonForInvestigation = formValues.reasonForInvestigation ?? '';
          this.complaint.investigationNarrative = formValues.investigationNarrative ?? '';
          this.complaint.triageDate = formValues.triageDate?.toDateString() ?? '';
          this.complaint.investigationDueDate = formValues.investigationDueDate?.toDateString() ?? '';
          this.complaint.triageDecision = formValues.triageDecision ?? '';
          this.complaint.investigationEntranceDate = formValues.investigationEntranceDate?.toDateString() ?? '';
          this.complaint.investigationExitDate = formValues.investigationExitDate?.toDateString() ?? '';
          this.complaint.investigationEndDate = formValues.investigationEndDate?.toDateString() ?? '';
          this.complaint.dateReportSent = formValues.dateReportSent?.toDateString() ?? '';
          this.complaint.planOfCorrectionDue = formValues.planOfCorrectionDue?.toDateString() ?? '';

          const complaintToSave = { ...this.complaint, closed: markClosed || this.complaint.closed };
          this.saving = true;
          const update = this.complaintService.updateComplaint(complaintToSave);
          const save = this.adding ? update.pipe(
            switchMap(response => this.complaintService.submitComplaint(complaintToSave).pipe(
              map(submitted => {
                if (!submitted) {
                  throw new Error('Complaint submission failed.');
                }
                return response;
              })
            ))
          ) : update;
          save.pipe(finalize(() => this.saving = false)).subscribe({
            next: (response: IComplaint) => {
              this.sharedService.toastr.success(markClosed
                ? 'Complaint closed successfully.'
                : this.adding ? 'Complaint created successfully.' : 'Complaint updated successfully.');
            },
            error: (error) => {
              console.log("Error updating complaint", error);
              this.sharedService.toastr.error('Error updating complaint.');
            },
            complete: () => {
              this.closeComplaint();
              this.loadComplaints();
            }
          });
        }
      }
    );
  }

  //Affected People Methods
  public addAffectedPerson(): void {
    this.personDialogRef = this.dialog.open(AffectedPersonDialogComponent, {
      data: { person: this.getEmptyAffectedPerson() },
      role: 'dialog',
      ariaLabel: "Confirmation Dialog",
      minWidth: '300px',
      maxWidth: !this.sharedService.mobile ? '700px' : '300px',
      maxHeight: this.sharedService.mobile ? '600px' : '700px',

    });

    this.personDialogRef.afterClosed().subscribe((result: IAffectedPerson) => {
      if (result) {
        this.complaint.affectedPeople.push(result);
        this.updateAffectedPeopleTable();
      }
    });
  }

  public editAffectedPerson(person: IAffectedPerson): void {
    this.personDialogRef = this.dialog.open(AffectedPersonDialogComponent, {
      data: { person: person },
      role: 'dialog',
      ariaLabel: "Confirmation Dialog",
      minWidth: '300px',
      maxWidth: !this.sharedService.mobile ? '700px' : '300px',
      maxHeight: this.sharedService.mobile ? '600px' : '700px',

    });

    this.personDialogRef.afterClosed().subscribe((result: IAffectedPerson) => {
      if (result) {
        const index = this.complaint.affectedPeople.findIndex(p => p.id === result.id);
        if (index !== -1) {
          this.complaint.affectedPeople[index] = result;
          this.updateAffectedPeopleTable();
        }
      }
    });
  }

  public deleteAffectedPerson(person: IAffectedPerson): void {
    this.sharedService.openConfirm('Delete the affected person?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          const index = this.complaint.affectedPeople.findIndex(p => p.id === person.id);
          if (index !== -1) {
            this.complaint.affectedPeople.splice(index, 1);
            this.updateAffectedPeopleTable();
          }
        }
      }
    );
  }

  private getEmptyAffectedPerson(): IAffectedPerson {
    let emptyPerson: IAffectedPerson = {
      id: 0,
      clientGuid: '',
      firstName: '',
      middleName: '',
      lastName: '',
      address: '',
      phone: '',
      email: '',
      dob: '',
      gender: '',
      relationship: '',
      relationshipOther: '',
      canContact: false,
      whyNoContact: '',
      otherContactReason: '',
      clientNarrative: '',
      clientResponded: false,
      servicesReceived: '',
      clientSchedule: '',
      incidentLocation: '',
      dateAdmitted: '',
      currentPatient: false,
      dateDischarged: ''
    }
    return emptyPerson;
  }
}
